import { useEffect } from 'react';
import { drawSymbolShape } from './symbol-renderer';
import { BUFFER_SIZE } from './types';
import type { PlacedSymbol, LandTool, MapSection, SymbolMode } from './types';

interface CursorPreviewInput {
    fgCanvasRef: React.RefObject<HTMLCanvasElement | null>;
    cursorPos: { x: number; y: number } | null;
    isHovering: boolean;
    activeSection: MapSection;
    symbolMode: SymbolMode;
    brushSize: number;
    activeSymbol: string;
    activeLandTool: LandTool;
    processedSheets: Record<string, HTMLCanvasElement | HTMLImageElement>;
    symbolSize: number;
    dimensions: { width: number; height: number };
    placedSymbols: PlacedSymbol[];
    activeSelectedUid: string | null;
    zoomRef: React.MutableRefObject<number>;
    panRef: React.MutableRefObject<{ x: number; y: number }>;
    isCustomUpload: boolean;
    customMapImageRef?: React.RefObject<HTMLImageElement | null>;
}

/**
 * Side-effect hook: renders cursor-hover previews and the selected-symbol
 * highlight ring onto the foreground canvas.  Extracted from map-canvas.tsx.
 */
export function useCursorPreview({
    fgCanvasRef,
    cursorPos,
    isHovering,
    activeSection,
    symbolMode,
    brushSize,
    activeSymbol,
    activeLandTool,
    processedSheets,
    symbolSize,
    dimensions,
    placedSymbols,
    activeSelectedUid,
    zoomRef,
    panRef,
    isCustomUpload,
    customMapImageRef,
}: CursorPreviewInput): void {
    useEffect(() => {
        const fg = fgCanvasRef.current;
        if (!fg) return;
        const ctx = fg.getContext('2d');
        if (!ctx) return;

        ctx.clearRect(0, 0, fg.width, fg.height);

        const zoom = zoomRef.current;
        const pan = panRef.current;
        const { width, height } = dimensions;

        const isCustom = isCustomUpload && !!customMapImageRef?.current;
        const mapWidth = isCustom ? (customMapImageRef?.current?.naturalWidth ?? BUFFER_SIZE) : BUFFER_SIZE;
        const mapHeight = isCustom ? (customMapImageRef?.current?.naturalHeight ?? BUFFER_SIZE) : BUFFER_SIZE;

        let renderWidth = Math.min(width, height);
        let renderHeight = Math.min(width, height);
        if (isCustom) {
            const ratio = mapWidth / mapHeight;
            if (width / height > ratio) { renderHeight = height; renderWidth = height * ratio; }
            else { renderWidth = width; renderHeight = width / ratio; }
        }

        // 1. Selected symbol highlight ring (always visible even without hover)
        if (activeSection === 'symbols' && symbolMode === 'select' && activeSelectedUid) {
            const selectedSym = placedSymbols.find(sym => sym.uid === activeSelectedUid);
            if (selectedSym) {
                ctx.save();
                ctx.strokeStyle = '#00a2ff';
                ctx.lineWidth = 2.5;
                ctx.setLineDash([4, 2]);
                const screenX = (selectedSym.x / mapWidth) * (renderWidth * zoom) + pan.x;
                const screenY = (selectedSym.y / mapHeight) * (renderHeight * zoom) + pan.y;
                const screenRadius = (selectedSym.size / 2) * zoom + 5;
                ctx.beginPath();
                ctx.arc(screenX, screenY, screenRadius, 0, Math.PI * 2);
                ctx.stroke();
                ctx.restore();
            }
        }

        // 2. Hover previews (only when mouse is inside the canvas)
        if (!cursorPos || !isHovering) return;

        if (activeSection === 'symbols' && symbolMode === 'select') {
            const bx = ((cursorPos.x - pan.x) / (renderWidth * zoom)) * mapWidth;
            const by = ((cursorPos.y - pan.y) / (renderHeight * zoom)) * mapHeight;
            const hoveredSym = [...placedSymbols].reverse().find(sym =>
                Math.hypot(sym.x - bx, sym.y - by) < sym.size / 2,
            );
            if (hoveredSym && hoveredSym.uid !== activeSelectedUid) {
                ctx.save();
                ctx.strokeStyle = 'rgba(0, 162, 255, 0.5)';
                ctx.lineWidth = 1.5;
                ctx.setLineDash([2, 2]);
                const screenX = (hoveredSym.x / mapWidth) * (renderWidth * zoom) + pan.x;
                const screenY = (hoveredSym.y / mapHeight) * (renderHeight * zoom) + pan.y;
                ctx.beginPath();
                ctx.arc(screenX, screenY, (hoveredSym.size / 2) * zoom + 4, 0, Math.PI * 2);
                ctx.stroke();
                ctx.restore();
            }
        } else if (activeSection === 'symbols' && (symbolMode === 'brush' || symbolMode === 'eraser')) {
            const radius = (brushSize * 10) * (renderWidth / mapWidth) * zoom;
            ctx.save();
            ctx.strokeStyle = symbolMode === 'eraser' ? 'rgba(255, 0, 0, 0.6)' : 'rgba(0, 0, 0, 0.8)';
            ctx.lineWidth = 2;
            ctx.setLineDash([4, 4]);
            ctx.beginPath();
            ctx.arc(cursorPos.x, cursorPos.y, radius, 0, Math.PI * 2);
            ctx.stroke();
            ctx.restore();
        } else if (activeSection === 'symbols' && symbolMode === 'single') {
            ctx.save();
            ctx.globalAlpha = 0.6;
            ctx.translate(cursorPos.x, cursorPos.y);
            ctx.scale((renderWidth / mapWidth) * zoom, (renderWidth / mapWidth) * zoom);
            drawSymbolShape(ctx, activeSymbol, 0, 0, symbolSize, processedSheets);
            ctx.restore();
        } else if (
            activeSection === 'land' &&
            (activeLandTool === 'raise' || activeLandTool === 'sink' || activeLandTool === 'paint' || activeLandTool === 'stamp')
        ) {
            const radius = (brushSize * 10) * (renderWidth / mapWidth) * zoom;
            ctx.save();
            ctx.strokeStyle = 'rgba(0, 0, 0, 0.6)';
            ctx.lineWidth = 2;
            ctx.setLineDash([4, 4]);
            ctx.beginPath();
            ctx.arc(cursorPos.x, cursorPos.y, radius, 0, Math.PI * 2);
            ctx.stroke();
            ctx.restore();
        }
    }, [
        fgCanvasRef, cursorPos, isHovering, activeSection, symbolMode, brushSize,
        activeSymbol, activeLandTool, processedSheets, symbolSize, dimensions,
        placedSymbols, activeSelectedUid, zoomRef, panRef, isCustomUpload, customMapImageRef,
    ]);
}
